import { useCallback, useState } from "react";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useObrasDocumentosPagination } from "../hooks/Obras_documentos/pagination";
import { useObrasInformacoesGeraisPagination } from "../hooks/Obras_informacoesgerais/pagination";
import { initialObraArquivo, ObraArquivoForm, ObraArquivoSchema } from "../schemas/Obras_informacoesgerais/obra_arquivo";
import { initialObrasDocumentosState, useObrasDocumentosFilters } from "../hooks/Obras_documentos/filters";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { ObraArquivo } from "../../domain/models/obra_arquivo";
import { useLocation } from "react-router";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";

/**
 * LoadObraArquivosFuncState
 */

type LoadObraArquivosFuncState = {
  idObra?: number;
  status?: number | null;
  situacao?: number | null;
  nomeDescricao?: string | null;
  dataVencimento?: (Date | null)[] | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useObrasDocumentosViewModel
 */

export const useObrasDocumentosViewModel = () => {

  const {
    obras: {
      getObraHeaderInfoUseCase,
      getAllObraArquivoUseCase,
      visualizeObraDocumentoUseCase,
      downloadObraDocumentoUseCase,
      updateObraArquivoUseCase,
      createObraArquivoUseCase,
      deleteObraArquivoUseCase,
    },
  } = useDependencyInjection();

  const {
    pushErrorNotification,
    setCustomLoading,
  } = usePageLoader();

  const debouncedHandler = useCreateDebounceHandler(750);

  const obrasTopHeaderState = useObrasTopHeaderState();
  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idObra, setIdObra] = useState<number>();

  const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();

  const [obraArquivoLista, setObraArquivoLista] = useState<ObraArquivo[]>();
  const [selectedObraArquivo, setSelectedObraArquivo] = useState<ObraArquivo>();

  const [obraArquivoModal, setObraArquivoModal] = useState(false);
  const [obraArquivoDeleteModal, setObraArquivoDeleteModal] = useState(false);

  const [obrasReportState, initializeReportState] = useObrasCommonReports({ idObra });

  const filters = useObrasDocumentosFilters();
  const pagination = useObrasDocumentosPagination();

  const hookForm = useForm<ObraArquivoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: initialObraArquivo,
    resolver: zodResolver(ObraArquivoSchema),
  });

  /**
   * Eventos
   */

  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObraHeaderInfo(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    idObra,
    getObraHeaderInfoUseCase,
  ]);

  const loadObraArquivos = useCallback(async (fs?: LoadObraArquivosFuncState) => {
    try {
      const filteredIdObra = fs?.idObra ?? idObra;
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);
      const filteredSituacao = evaluateParamValue(fs?.situacao, filters.situacao);
      const filteredNomeDescricao = evaluateParamValue(fs?.nomeDescricao, filters.nomeDescricao);
      const filteredDataVencimento = evaluateParamValue(fs?.dataVencimento, filters.dataVencimento);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllObraArquivoUseCase.getAllObraArquivo(
        filteredIdObra,
        filteredStatus,
        filteredSituacao,
        filteredNomeDescricao,
        filteredDataVencimento,
        filteredPage,
        filteredRowsPerPage,
      );

      setObraArquivoLista(getAllObraArquivoUseCase.items);
      pagination.setTotalItems(getAllObraArquivoUseCase.totalItems);
    } catch (e: unknown) {
      setObraArquivoLista(undefined);
      throw e;
    }
  }, [
    idObra,
    filters,
    pagination,
    getAllObraArquivoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id') ?? null;
      const locationIdObra = locationId ? Number(locationId) : undefined;
      const jobs = [];

      setIdObra(locationIdObra);

      jobs.push(loadObraHeaderInfo(locationIdObra));
      jobs.push(initializeReportState(locationIdObra));
      jobs.push(loadObraArquivos({ idObra: locationIdObra }));

      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    if (idObra) {
      try {
        setLoading(true);

        const jobs = [];

        jobs.push(loadObraHeaderInfo(idObra));
        jobs.push(initializeReportState(idObra));
        jobs.push(loadObraArquivos({ idObra }));

        await Promise.all(jobs);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!')[0]);
      } finally {
        setLoading(false);
      }
    }
  }, [
    idObra,
    initializeReportState,
    loadObraHeaderInfo,
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/Obras_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleCadastroObraArquivoOpenState = useCallback((idObraArquivo: number | null, openState: boolean) => {
    if (openState) {
      if (idObraArquivo && obraArquivoLista) {
        setSelectedObraArquivo(obraArquivoLista.find(x => x.idObraArquivo === idObraArquivo));
      } else {
        setSelectedObraArquivo(undefined);
      }
    }
    setObraArquivoModal(openState);
  }, [
    obraArquivoLista,
  ]);

  const handleObraArquivoSubmit = useCallback(async (form: ObraArquivoForm) => {
    if (idObra) {
      try {
        setSaving(true);
        if (selectedObraArquivo) {
          await updateObraArquivoUseCase.updateObraArquivo(
            selectedObraArquivo.idObraArquivo,
            form.statusArquivo,
            form.nomeDocumento,
            form.file,
            form.dataVencimento,
          );
        } else {
          await createObraArquivoUseCase.createObraArquivo(
            idObra,
            form.statusArquivo,
            form.nomeDocumento,
            form.file,
            form.dataVencimento,
          );
        }
        await loadObraArquivos();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir documento!'));
      } finally {
        setSaving(false);
        setObraArquivoModal(false);
      }
    }
  }, [
    idObra,
    selectedObraArquivo,
    createObraArquivoUseCase,
    updateObraArquivoUseCase,
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handleDeleteObraArquivoModalOpenState = useCallback((idObraArquivo: number | null, openState: boolean) => {
    if (openState) {
      if (idObraArquivo && obraArquivoLista) {
        setSelectedObraArquivo(obraArquivoLista.find(x => x.idObraArquivo === idObraArquivo));
      } else {
        setSelectedObraArquivo(undefined);
      }
    }
    setObraArquivoDeleteModal(openState);
  }, [
    obraArquivoLista,
  ]);

  const handleDeleteConfirmObraArquivo = useCallback(async () => {
    if (selectedObraArquivo) {
      try {
        setSaving(true);
        await deleteObraArquivoUseCase.deleteObraArquivo(selectedObraArquivo.idObraArquivo);
        await loadObraArquivos();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir documento!'));
      } finally {
        setSaving(false);
        setObraArquivoDeleteModal(false);
      }
    }
  }, [
    selectedObraArquivo,
    deleteObraArquivoUseCase,
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useObrasDocumentosPagination.setState({ page });
      await loadObraArquivos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados de arquivos de obra!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useObrasInformacoesGeraisPagination.getState().obraArquivosSetter.setPage(1);
      useObrasInformacoesGeraisPagination.getState().obraArquivosSetter.setRowsPerPage(rowsPerPage);
      await loadObraArquivos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados de arquivos de obra!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handleViewObraArquivo = useCallback(async (idObraArquivo: number, nomeArquivo: string) => {
    try {
      setCustomLoading(true);
      await visualizeObraDocumentoUseCase.visualizeObraDocumento(idObraArquivo, nomeArquivo);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao deletar baixar documento!"));
    } finally {
      setCustomLoading(false);
    }
  }, [
    visualizeObraDocumentoUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleDownloadObraArquivo = useCallback(async (idObraArquivo: number, nomeArquivo: string) => {
    try {
      setCustomLoading(true);
      await downloadObraDocumentoUseCase.downloadObraDocumento(idObraArquivo, nomeArquivo);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao deletar baixar documento!"));
    } finally {
      setCustomLoading(false);
    }
  }, [
    downloadObraDocumentoUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedHandler, async (status: number | null) => {
    useObrasDocumentosFilters.setState({ status });
    return async () => {
      try {
        setLoading(true);
        useObrasDocumentosPagination.setState({ page: 1 });
        await loadObraArquivos({ page: 1, status })
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handleSituacaoChange = useDebouncedCallback(debouncedHandler, async (situacao: number | null) => {
    useObrasDocumentosFilters.setState({ situacao });
    return async () => {
      try {
        setLoading(true);
        useObrasDocumentosPagination.setState({ page: 1 });
        await loadObraArquivos({ page: 1, situacao })
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handleNomeDescricaoChange = useDebouncedCallback(debouncedHandler, async (nomeDescricao: string | null) => {
    useObrasDocumentosFilters.setState({ nomeDescricao });
    return async () => {
      try {
        setLoading(true);
        useObrasDocumentosPagination.setState({ page: 1 });
        await loadObraArquivos({ page: 1, nomeDescricao })
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handleDataVencimentoChange = useDebouncedCallback(debouncedHandler, async (dataVencimento: (Date | null)[] | null) => {
    useObrasDocumentosFilters.setState({ dataVencimento });
    return async () => {
      try {
        setLoading(true);
        useObrasDocumentosPagination.setState({ page: 1 });
        await loadObraArquivos({ page: 1, dataVencimento })
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handleClearFilter = useCallback(async () => {
    try {
      setLoading(true);
      useObrasDocumentosFilters.setState({ ...initialObrasDocumentosState });
      useObrasDocumentosPagination.setState({ page: 1 });
      await loadObraArquivos({ ...initialObrasDocumentosState, page: 1 })
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadObraArquivos,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    idObra,
    selectedObraHeaderInfo: selectedObraHeaderInfo ?? null,
    obraArquivoLista: obraArquivoLista ?? [],
    selectedObraArquivo: selectedObraArquivo ?? null,
    obraArquivoModal,
    obraArquivoDeleteModal,
    obrasTopHeaderState,
    obrasReportState,
    filters,
    pagination,
    hookForm,
    handleReload,
    handleBack,
    handleCadastroObraArquivoOpenState,
    handleObraArquivoSubmit,
    handleDeleteObraArquivoModalOpenState,
    handleDeleteConfirmObraArquivo,
    handlePageChange,
    handleRowsPerPageChange,
    handleViewObraArquivo,
    handleDownloadObraArquivo,
    handleStatusChange,
    handleSituacaoChange,
    handleNomeDescricaoChange,
    handleDataVencimentoChange,
    handleClearFilter,
  };
}